/** XeAgents.ai - AI-Powered Customer Report Dialog **/
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Flame,
  ThermometerSun,
  Snowflake,
  TrendingUp,
  Target,
  AlertCircle,
  Lightbulb,
  MessageSquare,
  Clock,
  ArrowRight,
  Sparkles,
  Download,
  Copy,
  CheckCircle2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/date-format";

export interface CustomerReport {
  contactId: string;
  contactName: string;
  phone: string;
  email?: string;

  callStatus: string;
  callDuration: number;
  classification: string;
  sentiment: string;

  executiveSummary: string;
  conversationHighlights: string[];
  customerIntent: string;
  painPoints: string[];
  objections: string[];
  interests: string[];

  leadScore: number;
  leadQuality: 'hot' | 'warm' | 'cold';
  readinessLevel: string;
  buyingSignals: string[];

  nextSteps: string[];
  followUpPriority: 'high' | 'medium' | 'low';
  recommendedFollowUpTime: string;
  customizedPitch: string;

  keyQuotes: string[];
  emotionalTone: string;
  engagementLevel: string;

  generatedAt: string;
}

interface CustomerReportDialogProps {
  report: CustomerReport | null;
  open: boolean;
  onClose: () => void;
}

export function CustomerReportDialog({ report, open, onClose }: CustomerReportDialogProps) {
  const { toast } = useToast();

  if (!report) return null;

  const getLeadQualityIcon = (quality: string) => {
    switch (quality) {
      case 'hot':
        return <Flame className="h-5 w-5 text-red-500" />;
      case 'warm':
        return <ThermometerSun className="h-5 w-5 text-orange-500" />;
      case 'cold':
        return <Snowflake className="h-5 w-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getLeadQualityColor = (quality: string) => {
    switch (quality) {
      case 'hot':
        return 'bg-red-500';
      case 'warm':
        return 'bg-orange-500';
      case 'cold':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'neutral':
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      case 'negative':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
  };

  const downloadReport = () => {
    const reportText = `
AI CUSTOMER REPORT
==================

Customer: ${report.contactName}
Phone: ${report.phone}
${report.email ? `Email: ${report.email}` : ''}
Generated: ${formatDate(report.generatedAt, 'PPpp')}

LEAD SCORE: ${report.leadScore}/100 (${report.leadQuality.toUpperCase()})
SENTIMENT: ${report.sentiment}
FOLLOW-UP PRIORITY: ${report.followUpPriority.toUpperCase()}

EXECUTIVE SUMMARY
=================
${report.executiveSummary}

CUSTOMER INTENT
===============
${report.customerIntent}

KEY HIGHLIGHTS
==============
${report.conversationHighlights.map((h, i) => `${i + 1}. ${h}`).join('\n')}

PAIN POINTS
===========
${report.painPoints.map((p, i) => `${i + 1}. ${p}`).join('\n')}

OBJECTIONS
==========
${report.objections.length > 0 ? report.objections.map((o, i) => `${i + 1}. ${o}`).join('\n') : 'None identified'}

INTERESTS
=========
${report.interests.map((i, idx) => `${idx + 1}. ${i}`).join('\n')}

BUYING SIGNALS
==============
${report.buyingSignals.length > 0 ? report.buyingSignals.map((b, i) => `${i + 1}. ${b}`).join('\n') : 'None identified'}

RECOMMENDED NEXT STEPS
======================
${report.nextSteps.map((s, i) => `${i + 1}. ${s}`).join('\n')}

Follow-up Timing: ${report.recommendedFollowUpTime}

CUSTOMIZED SALES PITCH
======================
${report.customizedPitch}

KEY QUOTES
==========
${report.keyQuotes.length > 0 ? report.keyQuotes.map((q, i) => `${i + 1}. "${q}"`).join('\n') : 'None captured'}

EMOTIONAL TONE
==============
${report.emotionalTone}

ENGAGEMENT LEVEL
================
${report.engagementLevel}

READINESS LEVEL
===============
${report.readinessLevel}
    `.trim();

    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-report-${report.contactName.replace(/\s+/g, '-')}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Report Downloaded",
      description: "Customer report saved as text file",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-violet-500" />
                AI Customer Report
              </DialogTitle>
              <DialogDescription className="mt-1">
                {report.contactName} • {report.phone}
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={downloadReport}
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
            <TabsTrigger value="action">Action Plan</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4">
            {/* Lead Score Card */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getLeadQualityIcon(report.leadQuality)}
                    <div>
                      <CardTitle>Lead Score: {report.leadScore}/100</CardTitle>
                      <CardDescription>{report.readinessLevel}</CardDescription>
                    </div>
                  </div>
                  <Badge className={getLeadQualityColor(report.leadQuality)}>
                    {report.leadQuality.toUpperCase()} LEAD
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Sentiment:</span>
                    <Badge className={getSentimentColor(report.sentiment)}>
                      {report.sentiment}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Follow-up Priority:</span>
                    <Badge className={getPriorityColor(report.followUpPriority)}>
                      {report.followUpPriority.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Recommended Timing:</span>
                    <span className="font-medium">{report.recommendedFollowUpTime}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Executive Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed">{report.executiveSummary}</p>
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(report.executiveSummary, "Summary")}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Customer Intent */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-yellow-500" />
                  Customer Intent
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{report.customerIntent}</p>
              </CardContent>
            </Card>

            {/* Conversation Highlights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Conversation Highlights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {report.conversationHighlights.map((highlight, i) => (
                    <li key={i} className="flex gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* INSIGHTS TAB */}
          <TabsContent value="insights" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Pain Points */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-5 w-5" />
                    Pain Points
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.painPoints.length > 0 ? (
                    <ul className="space-y-2">
                      {report.painPoints.map((point, i) => (
                        <li key={i} className="text-sm flex gap-2">
                          <span className="text-red-500">•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No pain points identified</p>
                  )}
                </CardContent>
              </Card>

              {/* Interests */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-600">
                    <TrendingUp className="h-5 w-5" />
                    Interests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.interests.length > 0 ? (
                    <ul className="space-y-2">
                      {report.interests.map((interest, i) => (
                        <li key={i} className="text-sm flex gap-2">
                          <span className="text-green-500">•</span>
                          <span>{interest}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No specific interests identified</p>
                  )}
                </CardContent>
              </Card>

              {/* Objections */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-600">
                    <AlertCircle className="h-5 w-5" />
                    Objections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.objections.length > 0 ? (
                    <ul className="space-y-2">
                      {report.objections.map((objection, i) => (
                        <li key={i} className="text-sm flex gap-2">
                          <span className="text-orange-500">•</span>
                          <span>{objection}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No objections raised</p>
                  )}
                </CardContent>
              </Card>

              {/* Buying Signals */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-violet-600">
                    <Target className="h-5 w-5" />
                    Buying Signals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.buyingSignals.length > 0 ? (
                    <ul className="space-y-2">
                      {report.buyingSignals.map((signal, i) => (
                        <li key={i} className="text-sm flex gap-2">
                          <span className="text-violet-500">•</span>
                          <span>{signal}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No buying signals detected</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Key Quotes */}
            {report.keyQuotes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Key Quotes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {report.keyQuotes.map((quote, i) => (
                      <div key={i} className="border-l-2 border-violet-500 pl-4 py-2 bg-violet-50 dark:bg-violet-950/20">
                        <p className="text-sm italic">"{quote}"</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ACTION PLAN TAB */}
          <TabsContent value="action" className="space-y-4">
            {/* Next Steps */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRight className="h-5 w-5" />
                  Recommended Next Steps
                </CardTitle>
                <CardDescription>
                  Follow up: {report.recommendedFollowUpTime} • Priority: {report.followUpPriority.toUpperCase()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3">
                  {report.nextSteps.map((step, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-violet-500 text-white text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(report.nextSteps.join('\n'), "Next Steps")}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Action Items
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Customized Pitch */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-violet-500" />
                  Personalized Sales Approach
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{report.customizedPitch}</p>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(report.customizedPitch, "Sales Pitch")}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Pitch
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DETAILS TAB */}
          <TabsContent value="details" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Call Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="font-medium">{report.callStatus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span className="font-medium">{Math.floor(report.callDuration / 60)}m {report.callDuration % 60}s</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Classification:</span>
                    <Badge variant="outline">{report.classification}</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Customer Profile</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Emotional Tone:</span>
                    <span className="font-medium text-right max-w-[200px]">{report.emotionalTone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Engagement:</span>
                    <span className="font-medium text-right max-w-[200px]">{report.engagementLevel}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Report Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Generated:</span>
                  <span className="font-medium">{formatDate(report.generatedAt, 'PPpp')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contact ID:</span>
                  <span className="font-mono text-xs">{report.contactId}</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
